import * as THREE from 'three';
import { landingDistance } from './planetLanding';

/**
 * How far in front of the camera a selected artwork comes to rest, in the planet's own radii.
 *
 * One radius, which puts the work at roughly a third of the frame's height — big enough to read
 * as the subject, with enough of the planet left around it to keep the sense of where it came
 * from.
 */
export const CARD_GAP = 1;

/**
 * Where a selected artwork stops, measured from the planet's centre along the camera's view
 * axis, given the radius the camera just landed on.
 *
 * This is a distance in *radii* rather than in world units, and that is the whole point. Every
 * other length in this scene already scales with the radius it belongs to — the card is
 * `radius * 0.75` tall (AddImages.js), the ring it hangs on is a whole number of radii
 * (GlobalFunctions.js), and the camera lands at `landingDistance`, itself a multiple of the
 * radius — so a gap of a fixed number of radii is the same size, in the same place in frame, on a
 * body of any size. The artwork's apparent height comes out as `0.225 / (2 * CARD_GAP * tan(fov/2))`
 * of the frame whatever the radius is, because the radius cancels: it is what the card is scaled
 * by, and what the gap is scaled by. Nothing here needs a per-planet number.
 *
 * `zoom` is the one thing that is not the radius, and it is here for the same reason: a ring
 * carrying more works than `RING_RADIUS` seats at the preferred frame size gives its frames less
 * width, and the visitor may close on that ring until the artwork is the size it is everywhere
 * else — see `ringZoomFor`, whose answer MainScene passes in here. The card's resting place
 * is measured from the camera, so it has to travel with it: a card clicked while the camera has
 * closed on the ring would otherwise come to rest behind the viewer, and the lightbox would open
 * on an artwork that had already gone past.
 *
 * The camera's aspect goes through to `landingDistance` and not to the gap: the fov is the
 * vertical one, so a portrait viewport is the narrower of the two and only the aspect knows it.
 * The card's size in frame does not depend on the aspect at all — it hangs at a fixed number of
 * radii directly in front of the camera, so the vertical framing is the same on a phone as on a
 * wide screen.
 *
 * @param {number} [zoom] - radii the camera has closed on the ring; 0 at the landed position.
 */
export function cardRestDistance(radius, fovDegrees, near, aspect = 1, zoom = 0) {
    return landingDistance(radius, fovDegrees, near, aspect) - radius * (CARD_GAP + zoom);
}

// Held at module level: selecting an image is a once-per-click event rather than a per-frame
// one, but the arithmetic is the same shape as planetOcclusion's and there is no reason to
// allocate for it.
const _front = new THREE.Vector3();
const _z = new THREE.Vector3();
const _x = new THREE.Vector3();
const _y = new THREE.Vector3();
const _basis = new THREE.Matrix4();
const UP = new THREE.Vector3(0, 1, 0);

/**
 * The orientation that turns the artwork's readable side square to the camera, upright.
 *
 * The readable side is the card's **-Z**. `PlaneGeometry` faces +Z, and the card's geometry is
 * rotated by PI about Y when it is built (AddImages.js — the line whose own comment asks why it
 * is needed), which lands that face on -Z. It is the same fact that makes
 * `img.lookAt(planet.position)` face the work outward on the ring.
 *
 * So aiming the readable side at the camera means putting the card's local +Z directly opposite
 * the way the camera lies, and taking its local +X from the world's up — the same construction
 * three's own `lookAt` uses, in the same order (`x = up x z`, not `z x up`). That order is not
 * arbitrary: swapping it leaves the card upright by the letter of the arithmetic while turning
 * the artwork upside down, because it rolls the card 180 degrees about the axis it is being
 * aimed down.
 *
 * **Where the camera is straight up or straight down from the card there is no upright to
 * take**, because the world's up is parallel to the axis being aimed and the cross product
 * collapses. That is a real orientation with no preferred answer, not an error: any horizontal
 * will do, and +X is as good as any of them.
 *
 * A card at the camera's own position has no direction to face at all; it is answered with the
 * upright identity rather than a NaN, so a caller that somehow lands in that state gets a card
 * standing still rather than a mesh that disappears.
 */
export function cardFacingQuaternion(cardPosition, cameraPosition) {
    _front.subVectors(cameraPosition, cardPosition);

    if (_front.lengthSq() === 0) _front.set(0, 0, -1);
    else _front.normalize();

    _z.copy(_front).negate();

    if (Math.abs(_z.y) > 1 - 1e-9) _x.set(1, 0, 0);
    else _x.crossVectors(UP, _z).normalize();

    _y.crossVectors(_z, _x);
    _basis.makeBasis(_x, _y, _z);

    return new THREE.Quaternion().setFromRotationMatrix(_basis);
}

/**
 * How far the card has turned about its own face, in radians, at a point in its flight.
 *
 * Exactly one turn from the moment it is picked up to the moment it stops, and at a steady rate
 * — which is what makes the spin read the same every time: the artwork arrives at the
 * orientation it was aimed at with nothing left over, because a whole turn is the identity.
 * Turning it by anything else, or ending anywhere but on the end orientation, is what makes a
 * spin look like it is settling in a different place on each planet.
 */
export function cardSpinAngle(progress) {
    return progress * Math.PI * 2;
}

const _spinAxis = new THREE.Vector3();
const _spin = new THREE.Quaternion();

/**
 * One frame of a flight, between two poses: the artwork at `progress` along the way.
 *
 * Both halves of the motion are composed here rather than at the call site, so that they cannot
 * drift apart — first the turn that brings the artwork round to face the camera, then the spin
 * applied *to* that turn. Applying it second is what keeps it a turn about the card's own face
 * at every point of the flight; measuring it about the ring's axis instead is what tumbled the
 * off-axis cards and left half the ring back-facing, in a proportion set by how many cards the
 * planet happened to have.
 *
 * The spin is a whole number of turns and lands on the pose underneath it with nothing left over,
 * so the artwork stops where it was aimed instead of a few degrees off. `turns` is signed, which
 * is the whole of the difference between the flight out and the flight home: home is the same
 * turn the other way round, so the card is left hanging exactly as it was found.
 *
 * Written through out-parameters because this runs once a frame on the object whose own position
 * and quaternion are being animated — the two are handed in as `outPosition`/`outQuaternion`, and
 * every value is recomputed from the start pose, so a dropped frame cannot accumulate drift.
 */
export function cardFlightPose(outPosition, outQuaternion, startPosition, endPosition,
    startQuaternion, endQuaternion, progress, turns = 1) {

    outPosition.lerpVectors(startPosition, endPosition, progress);
    outQuaternion.slerpQuaternions(startQuaternion, endQuaternion, progress);

    // The axis the spin belongs on: the card's own face, as the turn above has just left it.
    _spinAxis.set(0, 0, 1).applyQuaternion(outQuaternion);
    _spin.setFromAxisAngle(_spinAxis, cardSpinAngle(progress) * turns);

    outQuaternion.multiply(_spin);
}
